import { type Selection } from '@noths/polaris-product-listings-urls';

import type { Facet } from 'src/redux/productsQuery/types/Facet';

/** A single `{ alias, value }` facet/filter option to add or remove. */
type FilterTarget = { alias: string; value: string };

/**
 * A declarative edit to apply to the selections derived from store state before
 * a URL is built:
 *  - `add` applies a facet/filter option. For a `single`-select facet this
 *    *replaces* any current value for that alias; for `multi` it is appended.
 *  - `remove` drops one option (and the whole selection once its last value goes).
 *  - `clear` drops every `/-/` filter selection, keeping only taxonomy levels.
 */
export type FilterMutation = { add: FilterTarget } | { remove: FilterTarget } | { clear: true };

export type ApplyFilterMutationContext = {
  /** Store facets, consulted only to resolve a facet's `single`/`multi` type. */
  facets: Facet[];
  /** Aliases that are taxonomy levels — kept by `clear`. */
  taxonomyAliases: readonly string[];
};

const removeValue = (selections: Selection[], { alias, value }: FilterTarget): Selection[] =>
  selections
    .map((selection) =>
      selection.alias === alias
        ? { ...selection, values: selection.values.filter((current) => current !== value) }
        : selection,
    )
    .filter((selection) => selection.values.length > 0);

const addValue = (
  selections: Selection[],
  { alias, value }: FilterTarget,
  facets: Facet[],
): Selection[] => {
  const existing = selections.find((selection) => selection.alias === alias);
  if (!existing) {
    return [...selections, { alias, values: [value] }];
  }

  // A single-select facet holds at most one value, so adding replaces it.
  const isSingle = facets.find((facet) => facet.alias === alias)?.type === 'single';
  const values = isSingle
    ? [value]
    : existing.values.includes(value)
    ? existing.values
    : [...existing.values, value];

  return selections.map((selection) =>
    selection.alias === alias ? { ...selection, values } : selection,
  );
};

/**
 * Apply a {@link FilterMutation} to the selections that will be handed to the URL
 * `build`. Returns the selections unchanged when no mutation is supplied.
 */
export const applyFilterMutation = (
  selections: Selection[],
  mutation: FilterMutation | undefined,
  { facets, taxonomyAliases }: ApplyFilterMutationContext,
): Selection[] => {
  if (!mutation) {
    return selections;
  }
  if ('clear' in mutation) {
    return selections.filter((selection) => taxonomyAliases.includes(selection.alias));
  }
  if ('remove' in mutation) {
    return removeValue(selections, mutation.remove);
  }
  return addValue(selections, mutation.add, facets);
};
